import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Layout, Empty, Row, Col, Typography, Avatar, Spin } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import MessageList from './MessageList/MessageList';
import { useTheme } from '../../../Shared/GlobalContext/ThemeContext';
import { useTranslation } from 'react-i18next';
import CountdownPill from '../../../Shared/CountdownPill';

const { Content } = Layout;
const { Text } = Typography;

// Typing animation keyframes
const typingAnimationStyle = `
  @keyframes typingAnimation {
    0% { transform: translateY(0); }
    50% { transform: translateY(-5px); }
    100% { transform: translateY(0); }
  }
`;

// Enhanced Typing indicator component with local styles - positioned on right (agent) side
const TypingIndicator = ({ isDarkMode }) => {
    const { t } = useTranslation();

    const bubbleStyle = {
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        backgroundColor: isDarkMode ? '#303030' : '#f0f0f0',
        borderRadius: '18px',
        padding: '10px 16px',
        maxWidth: '80%',
        marginRight: '12px',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
    };

    const bubblePointerStyle = {
        content: '""',
        position: 'absolute',
        bottom: '8px',
        right: '-6px',
        width: '12px',
        height: '12px',
        backgroundColor: isDarkMode ? '#303030' : '#f0f0f0',
        transform: 'rotate(45deg)',
        borderRadius: '2px',
    };

    const dotBaseStyle = {
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        backgroundColor: '#1890ff',
        display: 'inline-block',
        margin: '0 2px',
    };

    return (
        <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'flex-end',
            width: '100%',
            padding: '8px 0'
        }}>
            <style dangerouslySetInnerHTML={{ __html: typingAnimationStyle }} />
            <div style={bubbleStyle}>
                <div style={{ ...bubblePointerStyle }} />
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div 
                        style={{ 
                            ...dotBaseStyle, 
                            animation: 'typingAnimation 1s infinite ease-in-out',
                            animationDelay: '0s'
                        }} 
                    />
                    <div 
                        style={{ 
                            ...dotBaseStyle, 
                            animation: 'typingAnimation 1s infinite ease-in-out',
                            animationDelay: '0.2s'
                        }} 
                    />
                    <div 
                        style={{ 
                            ...dotBaseStyle, 
                            animation: 'typingAnimation 1s infinite ease-in-out',
                            animationDelay: '0.4s'
                        }} 
                    />
                    <Text style={{ marginLeft: '8px', color: '#1890ff' }}>
                        {t('agent_is_typing')}
                    </Text>
                </div>
            </div>
            <Avatar
                size="small"
                icon={<UserOutlined />}
                style={{ marginLeft: '8px' }}
            />
        </div>
    );
};

// Countdown indicator positioned similarly to typing indicator (shared pill)
const CountdownIndicator = ({ isDarkMode, seconds }) => (
    <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        width: '100%',
        padding: '8px 0'
    }}>
        <CountdownPill
            seconds={seconds}
            isDarkMode={isDarkMode}
            size="md"
            // Restart the pulse exactly when the displayed second changes.
            animationKey={seconds}
        />
    </div>
);

const MessageContent = ({
    screens,
    selectedConversation,
    messages,
    loading,
    isInitialLoading = false,
    isFetchingMoreTop = false,
    messageRefs,
    handleScroll,
    handleReplyClick,
    onReplyAction,
    setSelectedMessage,
    setDrawerVisible,
    hasMore,
    // keyboardInset, // Removed
    scrollApiRef
}) => {
    const { isDarkMode } = useTheme();
    const { t } = useTranslation();
    const contentBackgroundColor = isDarkMode ? '#1f1f1f' : '#fff';
    const isWriting = selectedConversation?.is_writing;
    const rawStartsIn = selectedConversation?.processing_starts_in ?? 0;
    const initialStartsIn = useMemo(() => {
        const next = parseInt(String(rawStartsIn), 10);
        return Number.isFinite(next) && next > 0 ? next : 0;
    }, [rawStartsIn]);
    const [secondsUntilProcessing, setSecondsUntilProcessing] = useState(initialStartsIn);
    const countdownEndsAtRef = useRef(0);
    const countdownVersionRef = useRef(0);
    const [countdownVersion, setCountdownVersion] = useState(0);

    // Optimize: Memoize countdown calculation
    const processedCountdown = useMemo(() => {
        if (isWriting) return 0;
        const next = parseInt(String(rawStartsIn), 10);
        return Number.isFinite(next) && next > 0 ? next : 0;
    }, [rawStartsIn, isWriting]);

    useEffect(() => {
        // Sync countdown with newest server value by anchoring to a real end timestamp.
        // This avoids drift/jitter and makes the visual pulse feel aligned with the number.
        const s = processedCountdown;
        if (!s || s <= 0) {
            countdownEndsAtRef.current = 0;
            setSecondsUntilProcessing(0);
            return;
        }
        countdownEndsAtRef.current = Date.now() + s * 1000;
        setSecondsUntilProcessing(s);
        countdownVersionRef.current += 1;
        setCountdownVersion(countdownVersionRef.current);
    }, [processedCountdown, selectedConversation?.id]);

    // Tick based on wall clock vs anchored end time (keeps countdown accurate).
    useEffect(() => {
        if (!countdownEndsAtRef.current) return;

        const id = setInterval(() => {
            const remainingMs = countdownEndsAtRef.current - Date.now();
            const next = remainingMs > 0 ? Math.ceil(remainingMs / 1000) : 0;
            setSecondsUntilProcessing((prev) => (prev === next ? prev : next));
            if (next <= 0) {
                countdownEndsAtRef.current = 0;
                clearInterval(id);
            }
        }, 200);

        return () => clearInterval(id);
    }, [countdownVersion]);

    // Prepare virtual list data including temporary indicators
    const virtualListData = useMemo(() => {
        const data = [];
        
        // Add typing indicator as first item if agent is writing
        if (isWriting && selectedConversation && messages.length > 0) {
            data.push({
                id: 'typing-indicator',
                type: 'typing-indicator',
                isTemporary: true,
                isDarkMode
            });
        }
        
        // Add countdown indicator as first item if countdown is active
        if (!isWriting && secondsUntilProcessing > 0 && selectedConversation && messages.length > 0) {
            data.push({
                id: 'countdown-indicator',
                type: 'countdown-indicator',
                isTemporary: true,
                isDarkMode,
                seconds: secondsUntilProcessing
            });
        }
        
        // Add all messages after the indicators
        data.push(...messages);
        
        return data;
    }, [messages, isWriting, selectedConversation, secondsUntilProcessing, isDarkMode]);

    return (
        <div style={{ position: 'relative', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Content
                theme={isDarkMode ? 'dark' : 'light'}
                style={contentStyle(screens, contentBackgroundColor)}
            >
                {!selectedConversation || !messages.length ? (
                    <EmptyState isDarkMode={isDarkMode} />
                ) : (
                    <>
                        <MessageList
                            messages={virtualListData}
                            loading={loading}
                            messageRefs={messageRefs}
                            handleReplyClick={handleReplyClick}
                            onReplyAction={onReplyAction}
                            setSelectedMessage={setSelectedMessage}
                            setDrawerVisible={setDrawerVisible}
                            screens={screens}
                            onScroll={handleScroll}
                            bottomInset={(screens.xs ? 12 : 24)}
                            hasMore={hasMore}
                            conversationId={selectedConversation?.id}
                            TypingIndicator={TypingIndicator}
                            CountdownIndicator={CountdownIndicator}
                            scrollApiRef={scrollApiRef}
                            isFetchingMoreTop={isFetchingMoreTop}
                        />
                    </>
                )}
            </Content>
            {selectedConversation && isInitialLoading && (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: isDarkMode ? 'rgba(0, 0, 0, 0.25)' : 'rgba(255, 255, 255, 0.6)',
                        zIndex: 3
                    }}
                >
                    <Spin size="large" />
                </div>
            )}
        </div>
    );
};

const contentStyle = (screens, contentBackgroundColor) => ({
    display: 'flex',
    flexDirection: 'column-reverse',
    flex: 1,
    minHeight: 0,
    backgroundColor: contentBackgroundColor,
});

const EmptyState = ({ isDarkMode }) => {
    const { t } = useTranslation();
    return (
    <Row justify="center" align="middle" style={{ height: '100%' }}>
        <Col>
            <Empty
                description={t('select_conversation_to_view_messages')}
                imageStyle={{
                    filter: isDarkMode ? 'invert(0.8)' : 'none'
                }}
            />
        </Col>
    </Row>
    );
};

export default MessageContent;
